import { createFileRoute } from "@tanstack/react-router"

import { TRIAL_DAYS } from "@/brand/plan-numbers"
import config from "@/config"
import { localizeHref } from "@/core/i18n/locale-context"
import { breadcrumbJsonLd, buildHead } from "@/core/seo"

import { Breadcrumb, CtaBand, SimpleTable } from "../../components/landing"
import { STORAGE_SENTENCE_ZH } from "../../content/zh/dates"
import { PROCESSOR_ROWS_ZH, TRUST_CHECKED_ZH, TRUST_ROWS_ZH } from "../../content/zh/trust"

/** Chinese version of /trust (D-WEB-LANG-SCOPE); the fact rows are translations of TRUST_FACTS and PROCESSORS. */
export const trustZhSeo = () =>
  buildHead({
    title: "安全、隐私与数据存放地",
    description: `${STORAGE_SENTENCE_ZH}我们收集什么、谁来处理、保存多久，以及如何导出或删除你的数据。`,
    path: "/trust",
    localePrefix: "zh",
    jsonLd: [breadcrumbJsonLd([{ name: "首页", path: "/zh" }, { name: "安全与隐私" }])],
  })

export const Route = createFileRoute("/_brand-layout/zh/trust")({
  head: () => {
    const { meta, links, scripts } = trustZhSeo()
    return { meta, links, scripts }
  },
  component: TrustZhPage,
})

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"

const rows = (facts: typeof TRUST_ROWS_ZH) =>
  facts.map((f) => ({
    header: f.label,
    cells: [
      f.value,
      <span key="source" className="text-muted-foreground">
        {f.href ? (
          <a
            href={f.href}
            className="hover:text-foreground focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
          >
            {f.sourceLabel}
          </a>
        ) : (
          f.sourceLabel
        )}
        ，核对于 {TRUST_CHECKED_ZH}
      </span>,
    ],
  }))

export function TrustZhPage() {
  return (
    <>
      <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "安全与隐私" }]} />
      <header className="bg-muted/40 px-band py-8 md:py-12">
        <h1 className="text-3xl font-bold tracking-tight md:text-5xl">我们如何保管你的记录</h1>
        <p className="text-muted-foreground mt-3 max-w-[60ch] text-lg">
          我们存了什么、存在哪里、还有谁会接触这些数据，以及你怎样带走或删除它们。也请读完整的
          <a href={localizeHref("/legal/privacy", "zh")} className={linkClass}>
            隐私政策（英文）
          </a>
          。
        </p>
      </header>

      <section className="px-band grid gap-4 py-8">
        <h2 className="text-2xl font-semibold">你的数据存放在哪里</h2>
        <SimpleTable
          caption="你的数据存放在哪里，以及保存多久"
          columns={["主题", "我们怎么做", "来源"]}
          rows={rows(TRUST_ROWS_ZH)}
        />
      </section>

      <section className="px-band grid gap-4 py-8">
        <h2 className="text-2xl font-semibold">还有谁处理数据</h2>
        <SimpleTable
          caption="还有谁处理数据"
          columns={["服务", "作用", "来源"]}
          rows={rows(PROCESSOR_ROWS_ZH)}
        />
        <p className="text-muted-foreground text-sm">
          对你的数据有疑问？请发邮件到{" "}
          <a href={`mailto:${config.core.supportEmail}`} className={linkClass}>
            {config.core.supportEmail}
          </a>
          。法律文件以英文版为准。
        </p>
      </section>

      <CtaBand
        title="用你自己的行程试一试"
        description={`免费试用 ${TRIAL_DAYS} 天，无需绑定银行卡。`}
        ctaLabel="开始免费试用"
        ctaHref="/login"
      />
    </>
  )
}
